import { fixNumbers, unhandledErrorGenerator, useFetchCallType } from '@rahtab/auth/lib/token-based-auth';
import { MapProps, useAlert } from '@rahtab/ui';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import { useViewer } from 'hooks/useViewer';
import React, { useEffect, useState } from 'react';
import { ScreenMainProps } from 'types/screens';
import { Guild, useGuilds } from '../../hooks/useGuilds';
import { RootStackType } from '../../Router';
import { County, Data, Province, ProvinceArg, useProvinces } from './useProvinces';

export const useEdit = (nextPage: 'goBack' | 'location' | 'home', provincesArgs?: ProvinceProps): UseEdit => {
  const { data: viewerData, loading: viewerLoading } = useViewer();
  const { getGuildLoading, guilds } = useGuilds();
  const { reset, goBack, navigate } = useNavigation<NavigationProp<RootStackType>>();
  const [provinceInitialized, setProvinceInitializes] = useState<{ city: boolean; county: boolean }>({
    city: false,
    county: false,
  });
  const [viewer, setViewer] = useState<EditProviderInfo>({
    address: '',
    city: '',
    county: '',
    email: '',
    firstName: '',
    guild: '',
    birthDate: '',
    guildNumber: '',
    identificationNumber: '',
    landlinePhoneNumber: '',
    lastName: '',
    mobileNumber: '',
    name: '',
    postalCode: '',
    province: '',
    region: {
      latitude: 0,
      latitudeDelta: 0,
      longitude: 0,
      longitudeDelta: 0,
    },
  });
  const locationInfo = useProvinces({
    selectedCounty: viewer.county,
    selectedProvince: viewer.province,
    skip: provincesArgs?.skipProvinceRequest,
  });
  const [image, setImage] = useState<ImagePicker.ImagePickerResult>({ cancelled: true });
  const alert = useAlert();
  const editProviderInfo: UseEdit['editProviderInfo'] = (key, value) => {
    setViewer((v) => ({
      ...v,
      [key]: value,
    }));
  };
  const [callEdit, { loading }] = useFetchCallType<{ result: boolean; error: string }>('EditProviderInfo', {
    skip: true,
    method: 'post',
    headers: { 'Content-Type': 'multipart/form-data' },
    onCompleted: (d) => {
      if (d.result) {
        if (nextPage === 'goBack') {
          goBack();
        } else if (nextPage === 'location') {
          navigate('CompleteLocationInfo');
        } else {
          reset({
            index: 0,
            routes: [{ name: 'Home' }],
          });
        }
      } else if (d.error) {
        alert({
          message: d.error,
        });
      } else {
        alert(unhandledErrorGenerator(505));
      }
    },
  });
  const edit: UseEdit['edit'] = () => {
    const args: {
      newProviderInfo: ServerEditObject;
      providerImage: string;
    } = {
      newProviderInfo: {
        BirthDate: viewer.birthDate,
        Email: viewer.email,
        FirstName: viewer.firstName,
        Guild: viewer.guild,
        GuildRegisterNumber: fixNumbers(viewer.guildNumber),
        LastName: viewer.lastName,
        MobileNo: fixNumbers(viewer.mobileNumber),
        NationalCode: fixNumbers(viewer.identificationNumber),
        Phone: fixNumbers(viewer.landlinePhoneNumber),
        PostalCode: fixNumbers(viewer.postalCode),
        ProviderName: viewer.name,
        Province: viewer.province,
        Latitude: viewer.region ? viewer.region.latitude.toString() : '0',
        Longitude: viewer.region ? viewer.region.longitude.toString() : '0',
        Address: viewer.address,
        City: viewer.city,
        County: viewer.county,
      },
      providerImage: !image.cancelled && image.base64 ? image.base64 : '',
    };
    let form = new FormData();
    form.append('ProviderJson', JSON.stringify(args.newProviderInfo));
    form.append('ProviderImage', args.providerImage);
    callEdit({
      data: form,
    });
  };
  useEffect(() => {
    if (!viewerLoading && viewerData) {
      setViewer({
        address: viewerData.address,
        city: viewerData.city,
        birthDate: viewerData.birthDate,
        county: viewerData.county,
        email: viewerData.email,
        firstName: viewerData.managerName,
        guild: viewerData.guild,
        guildNumber: viewerData.guildRegisterNumber,
        identificationNumber: viewerData.managerIdentificationNumber,
        landlinePhoneNumber: viewerData.storePhoneNumber,
        lastName: viewerData.managerLastName,
        mobileNumber: viewerData.managerMobileNumber,
        name: viewerData.name,
        postalCode: viewerData.postalCode,
        province: viewerData.province,
        region: {
          latitude: viewerData.latitude,
          longitude: viewerData.longitude,
          latitudeDelta: 0.0922,
          longitudeDelta: 0.0421,
        },
      });
      if (viewerData.image) {
        setImage({ cancelled: false, uri: viewerData.image, height: 0, width: 0, base64: '' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerLoading]);

  useEffect(() => {
    const county = locationInfo.counties[0];
    if (county) {
      setProvinceInitializes({
        city: false,
        county: true,
      });
      if (provinceInitialized.county || !viewer.county)
        setViewer((v) => ({
          ...v,
          county: county.value,
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationInfo.counties]);
  useEffect(() => {
    const city = locationInfo.cities[0];
    if (city) {
      setProvinceInitializes({ city: true, county: true });
      if (provinceInitialized.city || !viewer.city)
        setViewer((v) => ({
          ...v,
          city: city.value,
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationInfo.cities]);

  return {
    screenProps: {
      data: { ...viewer, image, guilds, locationInfo },
      loading: viewerLoading || !viewer.name || getGuildLoading || locationInfo.getProvincesLoading,
      transparentLoading: loading || locationInfo.changeDataLoading,
    },
    edit,
    editProviderInfo,
    setImage,
  };
};

interface ServerEditObject {
  BirthDate: string;
  ProviderName: string;
  Guild: string;
  GuildRegisterNumber: string;
  Phone: string;
  PostalCode: string;
  Address: string;
  Email: string;
  Province: string;
  County: string;
  City: string;
  Longitude: string;
  Latitude: string;
  FirstName: string;
  LastName: string;
  MobileNo: string;
  NationalCode: string;
}

interface EditProviderInfo {
  email: string;
  firstName: string;
  birthDate: string;
  guild: string;
  guildNumber: string;
  lastName: string;
  mobileNumber: string;
  identificationNumber: string;
  landlinePhoneNumber: string;
  postalCode: string;
  name: string;
  province: string;
  region: MapProps['region'];
  address: string;
  city: string;
  county: string;
}

interface UseEdit
  extends ScreenMainProps<
    EditProviderInfo & { image: ImagePicker.ImagePickerResult } & { guilds: Guild[] } & {
      locationInfo: {
        changeDataLoading: boolean;
        cities: Data[];
        counties: County[];
        getProvincesLoading: boolean;
        provinces: Province[];
      };
    }
  > {
  edit: () => void;
  editProviderInfo: (key: keyof EditProviderInfo, value: EditProviderInfo[typeof key]) => void;
  setImage: React.Dispatch<React.SetStateAction<ImagePicker.ImagePickerResult>>;
}

interface ProvinceProps extends Omit<ProvinceArg, 'skip'> {
  skipProvinceRequest?: boolean;
}
